import React, { useState, useEffect } from 'react';
import { DocumentChainItem, DocumentChainService, DocumentType } from '../../services/documentChainService';
import { 
  DocumentTextIcon, 
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface DocumentChainProps {
  documentId: string;
  documentType: DocumentType;
  showActions?: boolean;
  className?: string;
  compact?: boolean;
}

const DocumentChain: React.FC<DocumentChainProps> = ({
  documentId,
  documentType,
  showActions = false,
  className = '',
  compact = false
}) => {
  const [chain, setChain] = useState<DocumentChainItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDocumentChain();
  }, [documentId, documentType]);

  const loadDocumentChain = async () => {
    try {
      setLoading(true);
      const chainData = await DocumentChainService.getDocumentChain(documentId, documentType);
      setChain(chainData);
    } catch (error) {
      console.error('Error loading document chain:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeIcon = (type: DocumentType) => {
    switch (type) {
      case 'quotation':
        return DocumentTextIcon;
      case 'order':
        return DocumentTextIcon;
      case 'delivery_note':
        return DocumentTextIcon;
      case 'invoice':
        return DocumentTextIcon;
      default:
        return DocumentTextIcon;
    }
  };

  const getDocumentTypeLabel = (type: DocumentType) => {
    switch (type) {
      case 'quotation':
        return 'Árajánlat';
      case 'order':
        return 'Megrendelés';
      case 'delivery_note':
        return 'Szállítólevél';
      case 'invoice':
        return 'Számla';
      default:
        return 'Dokumentum';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'confirmed':
      case 'delivered':
      case 'success':
        return CheckCircleIcon;
      case 'pending':
      case 'draft':
        return ClockIcon;
      case 'cancelled':
      case 'rejected':
        return ExclamationTriangleIcon;
      default:
        return ClockIcon;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'confirmed':
      case 'delivered':
      case 'success':
        return 'text-green-600';
      case 'pending':
      case 'draft':
        return 'text-yellow-600';
      case 'cancelled':
      case 'rejected':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (chain.length === 0) {
    return (
      <div className={`text-gray-500 text-sm ${className}`}>
        Nincs bizonylat lánc információ
      </div>
    );
  }

  return (
    <div className={`${compact ? 'space-y-1' : 'space-y-2'} ${className}`}>
      {!compact && (
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Bizonylat lánc
        </h4>
      )}
      
      <div className="flex flex-wrap items-center gap-1.5">
        {chain.map((item, index) => {
          const Icon = getDocumentTypeIcon(item.type);
          const StatusIcon = getStatusIcon(item.status);
          const isCurrentDocument = item.id === documentId;
          
          return (
            <React.Fragment key={item.id}>
              {/* Dokumentum elem */}
              <div 
                className={`flex items-center space-x-1.5 ${compact ? 'px-2 py-1' : 'px-3 py-2'} rounded-lg border transition-all ${
                  isCurrentDocument 
                    ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
                    : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                }`}
              >
                <Icon className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-gray-600 dark:text-gray-400`} />
                <div className="flex flex-col">
                  {!compact && (
                    <span className={`text-xs font-medium ${
                      isCurrentDocument ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {getDocumentTypeLabel(item.type)}
                    </span>
                  )}
                  <span className={`${compact ? 'text-xs' : 'text-xs'} text-gray-500 dark:text-gray-400`}>
                    {item.number}
                  </span>
                </div>
                <StatusIcon className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} ${getStatusColor(item.status)}`} />
              </div>
              
              {/* Nyíl a következő elemhez */}
              {index < chain.length - 1 && (
                <ArrowRightIcon className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-gray-400`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      
      {!compact && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {chain.length} dokumentum a láncban
        </div>
      )}
    </div>
  );
};

export default DocumentChain; 